import {
  extractPlaceholdersFromMessage,
  getConditionalFieldOptions,
  getInvalidMessagePlaceholders,
  userOptions,
} from "./whatsappFlowCatalogs";

export const CONDITIONAL_OPERATORS = [
  { value: "=", label: "Igual (=)" },
  { value: "!=", label: "Distinto (!=)" },
  { value: "?", label: "Existe (?)" },
  { value: "!?", label: "No existe (!?)" },
  { value: ">", label: "Mayor (>)" },
  { value: ">=", label: "Mayor o igual (>=)" },
  { value: "<", label: "Menor (<)" },
  { value: "<=", label: "Menor o igual (<=)" },
];

const VALID_RECIPIENTS = new Set(userOptions.map((opt) => opt.value));

let conditionalIdCounter = 0;

export const createConditionalId = () => {
  conditionalIdCounter += 1;
  return `cond-${Date.now()}-${conditionalIdCounter}`;
};

export const operatorNeedsValue = (operator) => operator !== "?" && operator !== "!?";

export const createEmptyCondition = () => ({
  field: "",
  operator: "=",
  value: "",
});

export const createEmptyGroup = () => ({
  conditions: [createEmptyCondition()],
});

export const createEmptyConditionalMessage = () =>
  normalizeConditionalEntry({
    id: createConditionalId(),
    conditionalGroups: [],
    conditionalRecipient: "",
    conditionalMessage: "",
    conditionalParameterKeys: [],
    parseError: null,
  });

export const normalizeConditionalGroups = (groups) => {
  if (!Array.isArray(groups)) return [];
  return groups.map((group) => ({
    conditions: Array.isArray(group?.conditions)
      ? group.conditions.map((condition) => ({
          field: condition?.field ?? "",
          operator: condition?.operator ?? "=",
          value: condition?.value ?? "",
        }))
      : [],
  }));
};

export const normalizeConditionalEntry = (entry) => {
  const base = entry && typeof entry === "object" ? entry : {};
  const message = base.conditionalMessage ?? "";
  return {
    id: base.id || createConditionalId(),
    conditionalGroups: normalizeConditionalGroups(base.conditionalGroups),
    conditionalRecipient: base.conditionalRecipient ?? "",
    conditionalMessage: message,
    conditionalParameterKeys: Array.isArray(base.conditionalParameterKeys)
      ? base.conditionalParameterKeys
      : extractPlaceholdersFromMessage(message),
    parseError: base.parseError ?? base.conditionalParseError ?? null,
  };
};

export const normalizeConditionalMessages = (messages) => {
  if (!Array.isArray(messages)) return [];
  return messages.map((entry) => normalizeConditionalEntry(entry));
};

const serializeCondition = ({ field, operator, value }) => {
  const f = String(field || "").trim();
  const op = String(operator || "").trim();
  if (!f || !op) return "";
  if (op === "?") return `${f}?`;
  if (op === "!?") return `${f}!?`;
  if (!operatorNeedsValue(op)) return "";
  const v = value != null ? String(value).trim() : "";
  if (!v) return "";
  return `${f}${op}${v}`;
};

export const serializeConditionalGroups = (groups) => {
  const normalized = normalizeConditionalGroups(groups);
  const groupStrings = normalized
    .map((group) =>
      (group.conditions || [])
        .map(serializeCondition)
        .filter(Boolean)
        .join(";")
    )
    .filter(Boolean);
  return groupStrings.join(" | ");
};

const parseConditionAtom = (atom) => {
  const trimmed = String(atom || "").trim();
  if (!trimmed) return null;

  let match = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)!\?$/);
  if (match) return { field: match[1], operator: "!?", value: "" };

  match = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\?$/);
  if (match) return { field: match[1], operator: "?", value: "" };

  match = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)!=(.+)$/);
  if (match) return { field: match[1], operator: "!=", value: match[2].trim() };

  match = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)(>=|<=|>|<)(.+)$/);
  if (match) return { field: match[1], operator: match[2], value: match[3].trim() };

  match = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)=(.+)$/);
  if (match) return { field: match[1], operator: "=", value: match[2].trim() };

  return null;
};

export const parseConditionalWhen = (whenStr) => {
  if (whenStr == null || String(whenStr).trim() === "") {
    return { groups: [], parseError: null };
  }

  const groupParts = String(whenStr)
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);

  const groups = [];
  let parseError = null;

  for (const groupStr of groupParts) {
    const condParts = groupStr
      .split(";")
      .map((part) => part.trim())
      .filter(Boolean);
    const conditions = [];

    for (const atom of condParts) {
      const parsed = parseConditionAtom(atom);
      if (!parsed) {
        parseError = `No se pudo interpretar la condición: "${atom}"`;
        continue;
      }
      conditions.push(parsed);
    }

    if (conditions.length > 0) {
      groups.push({ conditions });
    }
  }

  return { groups, parseError };
};

const isConditionComplete = (condition) => {
  if (!condition?.field || !condition?.operator) return false;
  if (!operatorNeedsValue(condition.operator)) return true;
  return String(condition.value ?? "").trim() !== "";
};

export const hasNonEmptyConditionalGroups = (groups) => {
  const normalized = normalizeConditionalGroups(groups);
  if (normalized.length === 0) return false;
  return normalized.some(
    (group) =>
      Array.isArray(group.conditions) &&
      group.conditions.some(isConditionComplete)
  );
};

export const isConditionalEntryEmpty = (entry) => {
  const normalized = normalizeConditionalEntry(entry);
  const hasRecipient = Boolean(String(normalized.conditionalRecipient || "").trim());
  const hasMessage = Boolean(String(normalized.conditionalMessage || "").trim());
  const hasGroups = hasNonEmptyConditionalGroups(normalized.conditionalGroups);
  return !hasRecipient && !hasMessage && !hasGroups;
};

export const isConditionalEntryComplete = (entry) => {
  const normalized = normalizeConditionalEntry(entry);
  const hasRecipient = Boolean(String(normalized.conditionalRecipient || "").trim());
  const hasMessage = Boolean(String(normalized.conditionalMessage || "").trim());
  const hasGroups = hasNonEmptyConditionalGroups(normalized.conditionalGroups);
  return hasRecipient && hasMessage && hasGroups;
};

export const hasPartialConditionalEntry = (entry) => {
  if (isConditionalEntryEmpty(entry)) return false;
  return !isConditionalEntryComplete(entry);
};

const getAllowedFieldKeys = () =>
  new Set(getConditionalFieldOptions().map((opt) => opt.key));

export const validateConditionalEntry = (entry, label = "Mensaje condicional") => {
  if (isConditionalEntryEmpty(entry)) {
    return {
      ok: false,
      error: `${label}: completa destinatario, condiciones y mensaje, o elimina el bloque vacío.`,
    };
  }

  if (hasPartialConditionalEntry(entry)) {
    return {
      ok: false,
      error: `${label}: completa destinatario, condiciones y mensaje condicional.`,
    };
  }

  const normalized = normalizeConditionalEntry(entry);
  const allowedFields = getAllowedFieldKeys();
  const groups = normalized.conditionalGroups;

  if (groups.length === 0) {
    return {
      ok: false,
      error: `${label}: agrega al menos una condición válida.`,
    };
  }

  for (const group of groups) {
    if (!Array.isArray(group.conditions) || group.conditions.length === 0) {
      return {
        ok: false,
        error: `${label}: hay un grupo condicional vacío.`,
      };
    }

    for (const condition of group.conditions) {
      if (!condition.field) {
        return {
          ok: false,
          error: `${label}: selecciona el campo en todas las condiciones.`,
        };
      }
      if (!allowedFields.has(condition.field)) {
        return {
          ok: false,
          error: `${label}: el campo "${condition.field}" no está permitido.`,
        };
      }
      if (!condition.operator) {
        return {
          ok: false,
          error: `${label}: selecciona el operador en todas las condiciones.`,
        };
      }
      if (operatorNeedsValue(condition.operator) && !String(condition.value ?? "").trim()) {
        return {
          ok: false,
          error: `${label}: indica el valor para la condición de "${condition.field}".`,
        };
      }
      if (!isConditionComplete(condition)) {
        return {
          ok: false,
          error: `${label}: hay condiciones incompletas.`,
        };
      }
    }
  }

  const serialized = serializeConditionalGroups(groups);
  if (!serialized) {
    return {
      ok: false,
      error: `${label}: no se pudo generar la expresión condicional.`,
    };
  }

  const recipient = String(normalized.conditionalRecipient || "").trim();
  if (!VALID_RECIPIENTS.has(recipient)) {
    return {
      ok: false,
      error: `${label}: selecciona un destinatario condicional válido.`,
    };
  }

  const invalidPlaceholders = getInvalidMessagePlaceholders(
    normalized.conditionalMessage || ""
  );
  if (invalidPlaceholders.length > 0) {
    return {
      ok: false,
      error: `${label}: placeholders inválidos en el mensaje condicional: ${invalidPlaceholders.join(", ")}`,
    };
  }

  return { ok: true };
};

export const validateConditionalMessages = (stepData, stepLabel = "Paso") => {
  const messages = normalizeConditionalMessages(stepData?.conditionalMessages);
  if (messages.length === 0) {
    return { ok: true };
  }

  for (let index = 0; index < messages.length; index += 1) {
    const entryLabel = `${stepLabel}: mensaje condicional #${index + 1}`;
    const result = validateConditionalEntry(messages[index], entryLabel);
    if (!result.ok) return result;
  }

  return { ok: true };
};

export const validateAllFlowConditionals = (nodes) => {
  if (!Array.isArray(nodes)) return { ok: true };

  for (const node of nodes) {
    const stepLabel = node?.data?.name || node?.id || "Paso";
    const result = validateConditionalMessages(node?.data, stepLabel);
    if (!result.ok) return result;
  }

  return { ok: true };
};

export const apiItemToEditorEntry = (item) => {
  const whenStr = item?.conditionalWhen || "";
  const { groups, parseError } = parseConditionalWhen(whenStr);
  const message = item?.conditionalMessage || "";
  return normalizeConditionalEntry({
    id: createConditionalId(),
    conditionalGroups: groups,
    conditionalRecipient: item?.conditionalRecipient || "",
    conditionalMessage: message,
    conditionalParameterKeys: extractPlaceholdersFromMessage(message),
    parseError,
  });
};

export const migrateLegacyConditionalFields = (step) => {
  if (!step || typeof step !== "object") return [];
  const hasLegacy =
    Boolean(String(step.conditionalWhen || "").trim()) ||
    Boolean(String(step.conditionalRecipient || "").trim()) ||
    Boolean(String(step.conditionalMessage || "").trim());
  if (!hasLegacy) return [];
  return [
    apiItemToEditorEntry({
      conditionalWhen: step.conditionalWhen,
      conditionalRecipient: step.conditionalRecipient,
      conditionalMessage: step.conditionalMessage,
      sortOrder: 0,
    }),
  ];
};

export const parseApiConditionalMessages = (step) => {
  const fromApi = Array.isArray(step?.conditionalMessages)
    ? [...step.conditionalMessages]
    : [];
  if (fromApi.length > 0) {
    return fromApi
      .sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))
      .map((item) => apiItemToEditorEntry(item));
  }
  return migrateLegacyConditionalFields(step);
};

export const conditionalMessagesFromEditorData = (data) => {
  if (Array.isArray(data?.conditionalMessages)) {
    return normalizeConditionalMessages(data.conditionalMessages);
  }

  const hasLegacy =
    Boolean(String(data?.conditionalWhen || "").trim()) ||
    Boolean(String(data?.conditionalRecipient || "").trim()) ||
    Boolean(String(data?.conditionalMessage || "").trim()) ||
    hasNonEmptyConditionalGroups(data?.conditionalGroups);

  if (!hasLegacy) return [];

  const message = data?.conditionalMessage || "";
  const groups =
    Array.isArray(data?.conditionalGroups) && data.conditionalGroups.length > 0
      ? normalizeConditionalGroups(data.conditionalGroups)
      : parseConditionalWhen(data?.conditionalWhen || "").groups;

  return [
    normalizeConditionalEntry({
      id: createConditionalId(),
      conditionalGroups: groups,
      conditionalRecipient: data?.conditionalRecipient || "",
      conditionalMessage: message,
      conditionalParameterKeys: Array.isArray(data?.conditionalParameterKeys)
        ? data.conditionalParameterKeys
        : extractPlaceholdersFromMessage(message),
      parseError: data?.conditionalParseError ?? null,
    }),
  ];
};

export const serializeConditionalMessagesForPayload = (messages) => {
  const normalized = normalizeConditionalMessages(messages);
  return normalized.map((entry, index) => ({
    conditionalWhen: serializeConditionalGroups(entry.conditionalGroups),
    conditionalRecipient: String(entry.conditionalRecipient || "").trim(),
    conditionalMessage: entry.conditionalMessage,
    sortOrder: index,
  }));
};
